import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/lib/api";
import { BRAND_LOGO_URL } from "@/lib/brand";
import LanguageSwitcher from "@/components/app/LanguageSwitcher";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Lock, User, Loader2, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const expired = typeof window !== "undefined" && new URLSearchParams(window.location.search).get("expired") === "1";

  if (user) return <Navigate to={user.role === "admin" ? "/dashboard" : "/miei-lavori"} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const u = await login(identifier.trim(), password);
      toast.success(`Benvenuto, ${u.name}`);
      navigate(u.role === "admin" ? "/dashboard" : "/miei-lavori");
    } catch (e2) {
      const msg = formatApiError(e2);
      setErr(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left - brand panel */}
      <div className="hidden lg:flex lg:w-1/2 relative bg-slate-900 text-white overflow-hidden">
        <div
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage:
              "radial-gradient(circle at 20% 20%, #0284c7 0%, transparent 55%), radial-gradient(circle at 80% 80%, #0ea5e9 0%, transparent 50%)",
          }}
        />
        <div className="absolute inset-0 bg-[linear-gradient(rgba(2,132,199,0.07)_1px,transparent_1px),linear-gradient(90deg,rgba(2,132,199,0.07)_1px,transparent_1px)] bg-[size:40px_40px]" />
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          <div className="flex items-center">
            <div className="bg-white rounded-2xl p-3 shadow-xl">
              <img src={BRAND_LOGO_URL} alt="In & Out Traslochi" className="h-16 w-auto object-contain" />
            </div>
          </div>

          <div className="space-y-6">
            <h1 className="font-display text-4xl xl:text-5xl font-extrabold leading-[1.05] tracking-tight">
              Organizza ogni <span className="text-sky-400">trasloco</span>.
              <br />
              Senza stress, dal primo cliente all'ultimo scatolone.
            </h1>
            <p className="text-slate-300 text-base max-w-md leading-relaxed">
              Pianifica i turni, assegna le squadre, condividi i dettagli di ogni intervento con i tuoi dipendenti in tempo reale.
            </p>
            <div className="grid grid-cols-3 gap-4 pt-6 max-w-md">
              <div className="rounded-xl bg-white/5 border border-white/10 p-4 backdrop-blur">
                <div className="font-display text-2xl font-extrabold text-sky-400">24/7</div>
                <div className="text-xs text-slate-400 mt-1">Accesso dipendenti</div>
              </div>
              <div className="rounded-xl bg-white/5 border border-white/10 p-4 backdrop-blur">
                <div className="font-display text-2xl font-extrabold text-sky-400">A4</div>
                <div className="text-xs text-slate-400 mt-1">Foglio di lavoro</div>
              </div>
              <div className="rounded-xl bg-white/5 border border-white/10 p-4 backdrop-blur">
                <div className="font-display text-2xl font-extrabold text-sky-400">∞</div>
                <div className="text-xs text-slate-400 mt-1">Lavori gestibili</div>
              </div>
            </div>
          </div>

          <div className="text-xs text-slate-500 font-mono">© 2026 In & Out Traslochi</div>
        </div>
      </div>

      {/* Right - form */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12 bg-white">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-10 flex items-center">
            <div className="bg-white rounded-xl p-2 shadow ring-1 ring-slate-200">
              <img src={BRAND_LOGO_URL} alt="In & Out Traslochi" className="h-12 w-auto object-contain" />
            </div>
          </div>

          <div className="mb-6 flex justify-end">
            <div className="w-44"><LanguageSwitcher testid="login-language-switcher" /></div>
          </div>

          <div className="mb-8">
            <div className="text-xs uppercase tracking-widest text-sky-600 font-semibold mb-3">Accesso Riservato</div>
            <h2 className="font-display text-3xl font-extrabold text-slate-900 tracking-tight">Bentornato</h2>
            <p className="text-slate-500 mt-2 text-sm">Inserisci le tue credenziali per accedere al gestionale.</p>
          </div>

          {expired && (
            <div className="mb-5 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-sm text-amber-900" data-testid="session-expired-banner">
              🔒 La tua sessione è scaduta. Rifai il login per continuare.
            </div>
          )}

          <form onSubmit={submit} className="space-y-5" data-testid="login-form">
            <div>
              <Label htmlFor="identifier" className="text-slate-700 font-semibold text-sm">Email o Numero di Telefono</Label>
              <div className="mt-1.5 relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="identifier"
                  type="text"
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                  placeholder="email@azienda.it  oppure  +39 333 1234567"
                  className="pl-10 h-11"
                  data-testid="login-email-input"
                  autoComplete="username"
                  required
                />
              </div>
              <div className="text-[11px] text-slate-400 mt-1.5">
                Accedi con la tua email oppure con il numero di telefono
              </div>
            </div>
            <div>
              <Label htmlFor="password" className="text-slate-700 font-semibold text-sm">Password</Label>
              <div className="mt-1.5 relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="password"
                  type={showPwd ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="pl-10 pr-11 h-11"
                  data-testid="login-password-input"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPwd((v) => !v)}
                  aria-label={showPwd ? "Nascondi password" : "Mostra password"}
                  className="absolute right-1 top-1/2 -translate-y-1/2 p-2.5 rounded-md text-slate-400 hover:text-slate-700 transition-colors"
                  data-testid="login-toggle-password-button"
                >
                  {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            {err && (
              <div data-testid="login-error" className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">
                {err}
              </div>
            )}
            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit-button"
              className="w-full h-11 bg-sky-600 hover:bg-sky-700 text-white font-semibold shadow-md shadow-sky-600/30"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {loading ? "Accesso in corso…" : "Accedi"}
            </Button>
          </form>

          <div className="mt-5 flex items-center gap-3 text-xs text-slate-400">
            <div className="flex-1 h-px bg-slate-200" /> oppure <div className="flex-1 h-px bg-slate-200" />
          </div>
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
              const redirectUrl = window.location.origin + "/";
              window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
            }}
            className="mt-5 w-full h-11 font-semibold border-slate-300 hover:bg-slate-50"
            data-testid="login-google-button"
          >
            <svg className="w-4 h-4 mr-2" viewBox="0 0 48 48" aria-hidden="true"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.5 39.6 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z"/></svg>
            Accedi con Google
          </Button>
          <p className="mt-2 text-xs text-slate-400">Funziona solo con l'email Google registrata dall'amministratore.</p>
          <div className="mt-10 text-xs text-slate-400 font-mono">
            Non hai un account? Chiedilo al tuo amministratore.
          </div>
        </div>
      </div>
    </div>
  );
}
